import { isApolloError } from '@apollo/client';
import isEqual from 'fast-deep-equal';
import { useEffect, useState, useRef, useCallback } from 'react';
import { Maybe } from 'yup/es/types';

import {
  DocumentEdge,
  useDocumentsByClientIdsQuery,
  Document,
  Audit,
  Attachment,
} from '@graphql/generated';
import { AsyncStorage } from '@utils/storage';
import { isUrlExpired } from '@utils/urlExpiry';

// Re-exported for the many existing importers that reference it from this hook.
// The implementation now lives in the pure `@utils/urlExpiry` module.
export { isUrlExpired };

export const useGetDocumentsByClientIdsQuery = () => {
  const [clientIds, setClientIds] = useState<string[]>([]);
  const [inFlightRequests, setInFlightRequests] = useState<Set<string>>(
    new Set()
  );
  const [pendingBatch, setPendingBatch] = useState<Set<string>>(new Set());
  const batchTimeoutRef = useRef<number | null>(null);

  const {
    refetch: urlRefetch,
    data: refetchData,
    error: refetchError,
  } = useDocumentsByClientIdsQuery({
    variables: { clientIds },
    skip: clientIds.length == 0,
  });

  useEffect(() => {
    if (clientIds.length > 0) {
      urlRefetch();
    }
  }, [clientIds, urlRefetch]);

  useEffect(() => {
    if (refetchData?.documentsByClientIds) {
      const fetchedClientIds = refetchData.documentsByClientIds.map(
        (item) => item.clientId
      );

      const clearFetchedClientIds = () => {
        setInFlightRequests((prev) => {
          const newSet = new Set(prev);
          fetchedClientIds.forEach((id) => newSet.delete(id));
          return newSet;
        });
      };

      const cacheEntries = refetchData.documentsByClientIds
        .map((newItem): [string, string] | null => {
          const fileUrl = newItem.file?.url;
          return fileUrl ? [newItem.clientId, fileUrl] : null;
        })
        .filter((entry): entry is [string, string] => !!entry);

      if (cacheEntries.length === 0) {
        clearFetchedClientIds();
        return;
      }

      AsyncStorage.multiSet(cacheEntries)
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.log('Error caching document URLs', error);
        })
        .finally(clearFetchedClientIds);
    }
  }, [refetchData]);

  useEffect(() => {
    if (refetchError) {
      const isDocumentsNotFoundError =
        isApolloError(refetchError) &&
        refetchError.graphQLErrors?.some(
          (error) =>
            error.message?.includes('Documents not found') &&
            error.path?.includes('documentsByClientIds')
        );

      if (isDocumentsNotFoundError) {
        // eslint-disable-next-line no-console
        console.log(
          'Documents not found for client IDs (expected security behavior):',
          clientIds
        );
      } else {
        // eslint-disable-next-line no-console
        console.log(
          'Error from useDocumentsByClientIdsQuery',
          refetchError.message
        );
      }

      setInFlightRequests((prev) => {
        const newSet = new Set(prev);
        clientIds.forEach((id) => newSet.delete(id));
        return newSet;
      });
    }
  }, [refetchError, clientIds]);

  const updateClientIds = (newIds: string[]) => {
    if (isEqual(clientIds, newIds)) {
      return;
    }
    setClientIds(newIds);
  };

  const batchIndividualRequest = useCallback(
    (clientId: string) => {
      if (inFlightRequests.has(clientId)) {
        return Promise.resolve(null);
      }

      return new Promise<string | null>((resolve) => {
        setPendingBatch((prev) => new Set([...prev, clientId]));

        if (batchTimeoutRef.current) {
          clearTimeout(batchTimeoutRef.current);
        }

        batchTimeoutRef.current = window.setTimeout(async () => {
          const currentBatch = Array.from(pendingBatch);
          if (currentBatch.length === 0) return;

          setPendingBatch(new Set());

          setInFlightRequests((prev) => new Set([...prev, ...currentBatch]));

          updateClientIds(currentBatch);

          resolve(null);
        }, 100);
      });
    },
    [inFlightRequests, pendingBatch, updateClientIds]
  );

  function isDocumentEdge(edge: DocumentEdge | Document): edge is DocumentEdge {
    return '__typename' in edge && edge.__typename === 'DocumentEdge';
  }
  function isDocument(
    attachment: Maybe<Attachment> | undefined
  ): attachment is Document {
    return attachment?.__typename === 'Document';
  }
  const checkAuditsDocumentURL = (list: Audit[]) => {
    checkDocumentURL(list.map((t) => t.attachment).filter(isDocument));
    let docs: Document[] = [];
    list.forEach((a) => {
      if (a.attachment?.__typename === 'Message') {
        const tmps = a.attachment?.attachments?.filter(isDocument);
        const ids = docs.map((d) => d.clientId);
        const xtmp = tmps?.filter((t) => !ids.includes(t.clientId));
        if ((xtmp?.length ?? 0) > 0) docs = docs.concat(xtmp!);
      }
    });
    if (docs.length > 0) {
      checkDocumentURL(docs);
    }
  };

  const checkDocumentURL = async (edges: DocumentEdge[] | Document[]) => {
    if ((edges.length ?? 0) > 0) {
      let docs: Document[];
      if (isDocumentEdge(edges[0])) {
        docs = (edges as DocumentEdge[])
          .map((ed: DocumentEdge) => ed.node)
          .filter((doc): doc is Document => !!doc);
      } else {
        docs = edges as Document[];
      }
      const clientIdsNeedsToRefetchUrl = await checkDocumentClientIds(docs);
      if ((clientIdsNeedsToRefetchUrl?.needToRefetch?.length ?? 0) > 0) {
        updateClientIds(clientIdsNeedsToRefetchUrl?.needToRefetch ?? []);
      }
    }
  };

  const checkClientIds = async (clientIds: string[]) => {
    if ((clientIds.length ?? 0) > 0) {
      updateClientIds(clientIds);
    }
  };

  const checkDocumentClientIds = async (docs: Document[]) => {
    if ((docs.length ?? 0) > 0) {
      const clientIdsNeedsToRefetchUrl = new Set<string>();
      const clientIdsWithUrl: { clientId: string; url: string }[] = [];
      const now = new Date();
      for (const item of docs!) {
        if (
          item?.__typename === 'Document' &&
          item.clientId &&
          !inFlightRequests.has(item.clientId)
        ) {
          const url = await AsyncStorage.getItem(item.clientId);
          if (!url) {
            item.id !== item.clientId &&
              clientIdsNeedsToRefetchUrl.add(item.clientId);
          } else if (isUrlExpired(url, now)) {
            clientIdsNeedsToRefetchUrl.add(item.clientId);
          } else {
            clientIdsWithUrl.push({ clientId: item.clientId, url });
          }
        }
      }

      return {
        needToRefetch: [...clientIdsNeedsToRefetchUrl],
        urls: clientIdsWithUrl,
      };
    }
  };

  const cleanupStaleClientIds = async (failedClientIds: string[]) => {
    try {
      await AsyncStorage.multiRemove(failedClientIds);
      // eslint-disable-next-line no-console
      console.log('Cleaned up stale client IDs from cache:', failedClientIds);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error cleaning up stale client IDs:', error);
    }
  };

  useEffect(() => {
    return () => {
      if (batchTimeoutRef.current) {
        clearTimeout(batchTimeoutRef.current);
      }
    };
  }, []);

  return {
    checkAuditsDocumentURL,
    checkDocumentURL,
    checkClientIds,
    cleanupStaleClientIds,
    batchIndividualRequest,
    isRequestInFlight: (clientId: string) => inFlightRequests.has(clientId),
  };
};
